import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Seo from "@/components/Seo";
import { sortedPosts } from "@/content/blogPosts";
import { BLOG_TITLE, BLOG_DESCRIPTION, BLOG_KEYWORDS } from "@/content/siteMeta";
import { SITE_URL, SITE_NAME } from "@/lib/seo";

const fmt = (d) =>
  new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });

const schema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  name: `${SITE_NAME} Blog`,
  url: `${SITE_URL}/blog`,
  description:
    "Practical dental marketing guides for practice owners: new patient acquisition, Google Ads, local SEO, website conversion and the numbers that prove your marketing works.",
  publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  blogPost: sortedPosts.map((p) => ({
    "@type": "BlogPosting",
    headline: p.title,
    url: `${SITE_URL}/blog/${p.slug}`,
    datePublished: p.date,
    image: p.image,
    author: { "@type": "Organization", name: SITE_NAME },
  })),
};

export default function Blog() {
  const [featured, ...rest] = sortedPosts;

  return (
    <div className="min-h-screen bg-[#070B14] text-white font-[Manrope,sans-serif] overflow-x-hidden">
      <Seo
        title={BLOG_TITLE}
        description={BLOG_DESCRIPTION}
        path="/blog"
        keywords={BLOG_KEYWORDS}
        schema={schema}
      />
      <Nav />

      <main data-testid="blog-index-page" className="pt-32 md:pt-40">
        <section className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">
            / The Lab Notes
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tightest leading-[1.04] text-white max-w-3xl">
            Dental marketing,{" "}
            <span className="italic font-light text-white/60">written for owners</span> who
            want numbers.
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/60 max-w-2xl leading-relaxed">
            No fluff, no jargon. Just the systems, benchmarks and mistakes we see inside
            real dental practices, and exactly how to fix them.
          </p>
        </section>

        {/* Featured */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-card-${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-8 rounded-[2rem] border border-white/10 bg-white/[0.03] p-6 md:p-8 hover:border-amber-400/40 transition-colors duration-300"
            >
              <div className="lg:col-span-6 overflow-hidden rounded-2xl">
                <img
                  src={featured.image}
                  alt={featured.imageAlt}
                  loading="eager"
                  className="w-full h-64 md:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="lg:col-span-6 flex flex-col justify-center">
                <div className="flex items-center gap-3 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-amber-400">
                  <span>{featured.category}</span>
                  <span className="text-white/25">/</span>
                  <span className="text-white/40">{fmt(featured.date)}</span>
                </div>
                <h2 className="mt-5 font-display text-2xl md:text-3xl tracking-tight text-white leading-snug group-hover:text-amber-400 transition-colors duration-300">
                  {featured.title}
                </h2>
                <p className="mt-4 text-white/60 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-white">
                  Read the playbook
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                </span>
              </div>
            </Link>
          </motion.div>
        </section>

        {/* Grid */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group flex flex-col h-full rounded-3xl border border-white/10 bg-white/[0.025] overflow-hidden hover:border-amber-400/40 hover:bg-white/[0.05] transition-colors duration-300"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.imageAlt}
                    loading="lazy"
                    className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-amber-400">
                    <span>{p.category}</span>
                    <span className="text-white/25">/</span>
                    <span className="text-white/40">{p.readTime}</span>
                  </div>
                  <h2 className="mt-4 font-display text-xl tracking-tight text-white leading-snug group-hover:text-amber-400 transition-colors duration-300">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-white/55 leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <span className="mt-6 pt-4 border-t border-white/5 inline-flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.2em] text-white/70">
                    Read
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24 mb-24">
          <div className="relative overflow-hidden rounded-[2rem] border border-amber-400/25 bg-gradient-to-br from-amber-400/10 via-white/[0.02] to-transparent p-10 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-white leading-tight max-w-2xl mx-auto">
              Want this run on your practice instead of read about?
            </h2>
            <p className="mt-5 text-white/60 max-w-xl mx-auto">
              Thirty minutes, a live competitor breakdown and a realistic ninety-day
              new-patient forecast. Yours to keep either way.
            </p>
            <Link
              to="/#booking"
              data-testid="blog-index-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#070B14] px-7 h-12 font-medium hover:bg-amber-300 transition-colors duration-200"
            >
              Book a Strategy Call
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
